import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAdvisor } from '@/lib/advisor-context';
import type { ChatMessage, PrefKey, Preferences, QuickReply } from '@/lib/types';
import { briefPreviewSteps, findBriefChipPref, findBriefChipPrefForStep, isMultiBriefKey, type MultiBriefPref } from '@/lib/brief-options';
import { fetchAdvisorPreview } from '@/lib/advisor-api';
import { nextPrefKey } from '@/lib/chat-engine';
import { buildRefineChips, suggestedActionLabels } from '@/lib/search-refine';
import { resolveProjectMention } from '@/lib/board-route';
import { projectRefsFromCatalog } from '@/lib/engaged-projects';

export function ChatColumn() {
  const {
    messages,
    isLoading,
    shortlisting,
    catalogCount,
    prefs,
    sendText,
    setPref,
    toggleMultiPref,
    confirmMultiPrefStep,
    openBoardProject,
    setBoardView,
    showAllMatches,
    exploreMoreProjects,
    returnToMatchesHub,
    refineFromChip,
    beginBriefRefine,
    resetSession,
    matchProjects,
    board,
    briefPhase,
    mode,
    uiMode,
    preferenceEditingActive,
    lastSearchRecovery,
    lastNbaChips,
    lastNbaChipActions,
    activeMultiPref,
    briefOptions,
    compareAllMatches,
    planVisitDay,
    engagedProjects,
    visitRouteStopIds,
    focusedProject,
    signalSnapshot,
  } = useAdvisor();
  const [input, setInput] = useState('');
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isLoading]);

  const options = briefOptions;

  // While a turn is in flight, paint the project named in the latest user line
  // (e.g. "Tell me about Orchards") — not the stale board focus (Eldorado).
  const revealProject = useMemo(() => {
    if (!isLoading || shortlisting) return null;
    const lastUser = [...messages].reverse().find((m) => m.role === 'user')?.text ?? '';
    const mention = resolveProjectMention(lastUser, projectRefsFromCatalog(matchProjects));
    if (mention) {
      return matchProjects.find((p) => p.id === mention.id) ?? focusedProject ?? null;
    }
    return focusedProject ?? null;
  }, [isLoading, shortlisting, messages, matchProjects, focusedProject]);

  const suggestions = useMemo((): string[] => {
    const chipOpts = { serverActionsOnly: mode === 'live' };
    const lastBot = [...messages].reverse().find((m) => m.role === 'bot');
    const serverActions = lastSearchRecovery?.suggested_actions ?? lastBot?.suggestedActions;
    if (preferenceEditingActive || uiMode === 'search_recovery') {
      const fromServer = suggestedActionLabels(serverActions, prefs, options, chipOpts);
      if (fromServer.length > 0) {
        return uiMode === 'preference_refine'
          ? [...fromServer, 'Show me more projects']
          : fromServer;
      }
      // P7 rails entered refine without server recovery envelope — local brief chips
      if (uiMode === 'preference_refine') {
        return [...buildRefineChips(prefs, options), 'Show me more projects'].slice(0, 6);
      }
    }
    if (briefPhase === 'collecting') {
      if (activeMultiPref) return [];
      const next = nextPrefKey(prefs, options);
      if (next && options[next]) return [...(options[next] as readonly string[])];
      return [];
    }
    // Contextual sub-queries derived from what Naya understands — appended
    // AFTER server chips, deduped, capped, so server taxonomy always leads.
    const contextual: string[] = [];
    if (signalSnapshot) {
      const focusName = focusedProject?.name;
      if (signalSnapshot.schools || signalSnapshot.worries?.match(/school/i)) {
        contextual.push(focusName ? `Schools near ${focusName}` : 'Schools near my matches');
      }
      if (signalSnapshot.commute_hub) {
        contextual.push(
          focusName
            ? `Commute from ${signalSnapshot.commute_hub} to ${focusName}`
            : `Commute from ${signalSnapshot.commute_hub}`,
        );
      }
      if (signalSnapshot.worries?.match(/builder/i) && focusName) {
        contextual.push(`Who is the builder of ${focusName}?`);
      }
    }
    const withContextual = (base: string[]): string[] => {
      const seen = new Set(base.map((c) => c.toLowerCase()));
      const extra = contextual.filter((c) => !seen.has(c.toLowerCase())).slice(0, 2);
      return [...base, ...extra];
    };
    // P7: after brief, prefer server nba chips in live mode
    if (mode === 'live' && lastNbaChips && lastNbaChips.length > 0) {
      return withContextual(lastNbaChips);
    }
    if (board.view === 'visit') {
      return ['Back to my matches', 'Legal status of my picks', 'Compare all 3'];
    }
    if (board.view === 'compare') {
      return ['Back to my matches', 'Which fits my budget best?', 'Plan a visit day'];
    }
    if (board.view === 'project') {
      if (preferenceEditingActive || (lastSearchRecovery?.suggested_actions?.length ?? 0) > 0) {
        const refine = suggestedActionLabels(lastSearchRecovery?.suggested_actions, prefs, options, chipOpts);
        if (refine.length > 0) {
          return [...refine, 'Back to my matches', 'Compare all 3'];
        }
      }
      return ['Back to my matches', 'Compare all 3', 'Plan a visit day'];
    }
    if (board.view === 'concierge') return ['Plan a visit day', 'Back to my matches'];
    // The flag is authoritative. It used to be double-checked against the
    // reply's wording, which meant rewording a sentence silently hid the
    // refine chips. The engine states this now (suggest_refine).
    const needsRefine = lastBot?.suggestRefine === true;
    if (needsRefine) {
      const lastActions = lastBot?.suggestedActions ?? lastSearchRecovery?.suggested_actions;
      const refineChips = suggestedActionLabels(lastActions, prefs, options, chipOpts);
      if (refineChips.length > 0) {
        return [...refineChips, 'Show me more projects'];
      }
    }
    if (briefPhase === 'focused') {
      const configs = focusedProject?.configurations ?? [];
      const configChips = configs.slice(0, 4).map((c) => {
        const size = c.sizeDisplay ? ` · ${c.sizeDisplay}` : '';
        return `${c.unitType}${size}`;
      });
      return withContextual([
        ...configChips,
        'Is the title clear?',
        'Starting prices',
        'Back to all matches',
        'Show me more projects',
      ]);
    }
    const chips: string[] = [];
    if (matchProjects.length >= 2) {
      chips.push(`Compare all ${matchProjects.length}`);
      chips.push('Which fits my budget best?');
    }
    chips.push('Show me more projects');
    if (matchProjects.length > 0) chips.push('Open a project on the board');
    chips.push('Plan a visit day');
    return chips;
  }, [prefs, board.view, briefPhase, uiMode, mode, preferenceEditingActive, lastSearchRecovery, lastNbaChips, activeMultiPref, options, matchProjects.length, messages, focusedProject, signalSnapshot]);

  const multiOptions =
    activeMultiPref === 'bhk' ||
    activeMultiPref === 'propertyType' ||
    activeMultiPref === 'plotSize' ||
    activeMultiPref === 'worries'
      ? [...(options[activeMultiPref] ?? [])]
      : [];
  const multiSelected =
    activeMultiPref === 'bhk'
      ? prefs.bhkSelections
      : activeMultiPref === 'plotSize'
        ? prefs.plotSizeSelections
        : activeMultiPref === 'propertyType'
          ? prefs.propertyTypes
          : activeMultiPref === 'worries'
            ? prefs.worries
            : [];

  // Initial brief is chip-led only — free text collides with step prompts
  // (prefs ack with no shortlist). Unlock once brief_collect ends.
  const briefComposerLocked =
    !preferenceEditingActive && briefPhase === 'collecting' && uiMode === 'brief_collect';

  const onSend = () => {
    if (briefComposerLocked) return;
    const t = input.trim();
    if (!t) return;
    setInput('');
    sendText(t);
  };

  const onSuggestion = (label: string) => {
    // ADR-006: a content chip carries a server action_id → tap goes deterministic
    // (door skips the LLM extract). Rails have none and keep their local handlers below.
    const chipAction = lastNbaChipActions?.[label];
    if (label === 'Back to all matches' || label === 'Back to my matches') {
      returnToMatchesHub();
      return;
    }
    if (label === 'Start over') {
      resetSession();
      return;
    }
    if (label === 'Refine my brief') {
      beginBriefRefine('general');
      return;
    }
    if (label === 'Change area') {
      beginBriefRefine('area');
      return;
    }
    if (label === 'Adjust budget') {
      beginBriefRefine('budget');
      return;
    }
    if (label === 'Widen my search') {
      beginBriefRefine('widen');
      return;
    }
    const focusOn = /^Focus on (.+)$/i.exec(label.trim());
    if (focusOn?.[1]) {
      const name = focusOn[1].trim();
      const hit = matchProjects.find((p) => p.name.toLowerCase() === name.toLowerCase());
      if (hit) {
        openBoardProject(hit.id);
        sendText(`Tell me more about ${hit.name}`);
        return;
      }
      sendText(`Tell me more about ${name}`);
      return;
    }
    if (label === 'Legal status of my picks') {
      const first = matchProjects[0];
      if (first) sendText(`Is the title clear for ${first.name}?`);
      return;
    }
    if (label === 'Compare all 3') {
      compareAllMatches(chipAction);
      return;
    }
    if (label === 'Show me more projects') {
      exploreMoreProjects(chipAction);
      return;
    }
    if (refineFromChip(label)) return;
    if (label === 'Open a project on the board') {
      showAllMatches();
      return;
    }
    if (label === 'Plan a visit day') {
      planVisitDay(chipAction);
      return;
    }
    if (label === 'Explore another match') {
      setBoardView('matches');
      sendText('Show me other projects that fit my brief');
      return;
    }
    // Focused config chip: "2 BHK · 740-1043 sqft" → ask about that configuration
    if (focusedProject && briefPhase === 'focused') {
      const configs = focusedProject.configurations ?? [];
      const hit = configs.find((c) => {
        const size = c.sizeDisplay ? ` · ${c.sizeDisplay}` : '';
        return `${c.unitType}${size}` === label || c.unitType === label;
      });
      if (hit) {
        sendText(`What are the ${hit.unitType} options at ${focusedProject.name}?`);
        return;
      }
    }
    // P7 facet chips — bind to focused project so Spine never treats the label as a search query
    if (
      focusedProject &&
      (briefPhase === 'focused' || board.view === 'project') &&
      (/^(?:Starting prices|Pricing|Legal status|Legal details|What banks\?|Is EC clear\?|Send brochure|Unit configurations|Location & connectivity|Location and connectivity)$/i.test(
        label.trim(),
      ) ||
        new RegExp(`^Schools near ${focusedProject.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`, 'i').test(
          label.trim(),
        ) ||
        /^Schools near (?:my matches|this project)$/i.test(label.trim()) ||
        /^Commute from /i.test(label.trim()))
    ) {
      const schoolsNearFocus = /^Schools near /i.test(label.trim());
      const commuteNearFocus = /^Commute from /i.test(label.trim());
      sendText(
        schoolsNearFocus
          ? (label.toLowerCase().includes(focusedProject.name.toLowerCase()) ? label : `Schools near ${focusedProject.name}`)
          : (commuteNearFocus && !label.toLowerCase().includes(focusedProject.name.toLowerCase()))
            ? `${label} to ${focusedProject.name}`
            : (label.toLowerCase().includes(focusedProject.name.toLowerCase()) ? label : `${label.trim()} for ${focusedProject.name}`),
        chipAction,
      );
      return;
    }
    if (briefPhase === 'collecting' && uiMode === 'brief_collect') {
      const chip = findBriefChipPrefForStep(label, prefs, options);
      if (chip) {
        if (isMultiBriefKey(chip.pref)) {
          toggleMultiPref(chip.pref as MultiBriefPref, chip.value);
        } else {
          setPref(chip.pref, chip.value);
        }
        return;
      }
    }
    sendText(label, chipAction);
  };

  return (
    <div className="na-chat-col">
      <div ref={scrollerRef} className="na-scroll na-chat-scroll">
        <div className="na-chat-inner">
          {messages.map((m, i) => (
            <MessageBlock
              key={m.id}
              message={m}
              latest={i === messages.length - 1}
              onOpenProject={openBoardProject}
            />
          ))}
          {isLoading &&
            (shortlisting ? (
              <ThinkingStrip prefs={prefs} start={catalogCount} />
            ) : revealProject ? (
              // Track C — focused answer: paint the project named in this turn
              // (or board focus), then land the answer. Never blank / never stale.
              <AnswerReveal
                name={revealProject.name}
                microMarket={revealProject.location}
                facet={facetWorkingLabel(
                  [...messages].reverse().find((mm) => mm.role === 'user')?.text ?? '',
                )}
              />
            ) : (
              <TypingIndicator />
            ))}
        </div>
      </div>

      <div className="na-composer-wrap">
        <div className="na-chat-inner">
          {activeMultiPref && multiOptions.length > 0 && (
            <div className="na-suggestions na-multi-brief">
              <span className="na-multi-brief-label">Select all that apply</span>
              {multiOptions.map((label) => {
                const selected = multiSelected.includes(label);
                return (
                  <button
                    key={label}
                    type="button"
                    className={`na-sug-chip${selected ? ' na-sug-chip-on' : ''}`}
                    onClick={() => toggleMultiPref(activeMultiPref as MultiBriefPref, label)}
                  >
                    {label}
                  </button>
                );
              })}
              <button
                type="button"
                className="na-sug-chip na-sug-chip-continue"
                disabled={multiSelected.length === 0}
                onClick={() => confirmMultiPrefStep(activeMultiPref as MultiBriefPref)}
              >
                Continue
              </button>
            </div>
          )}

          {!activeMultiPref && suggestions.length > 0 && (
            <div className="na-suggestions">
              {suggestions.map((label) => (
                <button
                  key={label}
                  type="button"
                  className="na-sug-chip"
                  onClick={() => onSuggestion(label)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className={`na-composer${briefComposerLocked ? ' na-composer-locked' : ''}`}>
            <input
              value={input}
              disabled={briefComposerLocked}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onSend();
                }
              }}
              placeholder={
                briefComposerLocked
                  ? 'Use the choices above to continue'
                  : 'Ask Naya anything — or describe what you\'re after'
              }
              aria-disabled={briefComposerLocked}
            />
            <button
              type="button"
              className="na-send-btn"
              onClick={onSend}
              disabled={briefComposerLocked}
              aria-label={briefComposerLocked ? 'Send locked during brief' : 'Send'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 19V5" />
                <path d="m5 12 7-7 7 7" />
              </svg>
            </button>
          </div>
          <div className="na-composer-foot">
            {briefComposerLocked
              ? 'Tap a choice above — free chat unlocks after your brief'
              : 'Anonymous · your number is revealed only when you choose to visit'}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Spine replies carry WhatsApp-style `*bold*` emphasis — render those spans as <strong>. */
function botText(text: string): ReactNode {
  const parts = text.split(/(\*[^*\n]+\*)/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    part.length > 2 && part.startsWith('*') && part.endsWith('*') ? (
      <strong key={i}>{part.slice(1, -1)}</strong>
    ) : (
      part
    ),
  );
}

/** Track C — a friendly "what I'm doing" label from the buyer's own ask.
 *  Presentation only (never routing): if nothing matches, a generic line. */
function facetWorkingLabel(text: string): string {
  const t = text.toLowerCase();
  if (/\b(legal|rera|khata|title|encumbrance|\bec\b|approval)\b/.test(t)) return 'checking the legal pack…';
  if (/\b(price|pricing|cost|how much|charge|lakh|crore)\b/.test(t) || t.includes('₹')) return 'pulling the pricing…';
  if (/\b(unit|config|configuration|bhk|sqft|size|carpet|floor\s?plan)\b/.test(t)) return 'listing the configurations…';
  if (/\b(location|connect|metro|school|hospital|nearby|distance|airport)\b/.test(t)) return 'mapping the location…';
  if (/\b(amenit|clubhouse|pool|gym|facilit)\b/.test(t)) return 'checking the amenities…';
  if (/\b(emi|loan|installment|instalment|down\s?payment)\b/.test(t)) return 'working the numbers…';
  if (/\b(photo|image|brochure|gallery|video|media)\b/.test(t)) return 'finding the media…';
  return 'pulling the details…';
}

/** Identity-first reveal shown while a FOCUSED answer composes. Paints the
 *  project the buyer is already on (name · micro-market, from state — no fetch)
 *  instantly, so the screen is never blank; the grounded answer lands after. */
function AnswerReveal({
  name,
  microMarket,
  facet,
}: {
  name: string;
  microMarket?: string;
  facet: string;
}) {
  return (
    <div className="na-msg">
      <div className="na-msg-row">
        <Avatar />
        <div className="na-answer-reveal">
          <div className="na-ar-id">
            <span className="na-ar-name">{name}</span>
            {microMarket ? <span className="na-ar-mm">{microMarket}</span> : null}
          </div>
          <div className="na-ar-working">
            <span className="na-ar-dots">
              <span />
              <span />
              <span />
            </span>
            <span className="na-ar-facet">{facet}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The newest bot reply, revealed line-by-line — the honest "streaming": the
 *  whole grounded answer is already present, each line just fades in fastest-
 *  first. Nothing is painted that wasn't composed. */
function RevealText({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <span
          key={i}
          className="na-reveal-line"
          style={{ animationDelay: `${Math.min(i * 55, 440)}ms` }}
        >
          {line.trim() ? botText(line) : ' '}
        </span>
      ))}
    </>
  );
}

function MessageBlock({
  message: m,
  latest = false,
  onOpenProject,
}: {
  message: ChatMessage;
  latest?: boolean;
  onOpenProject: (id: string) => void;
}) {
  if (m.receipt) {
    return <ReceiptLine receipt={m.receipt} />;
  }
  if (m.projects?.length) {
    return (
      <div className="na-msg">
        <div className="na-msg-row">
          <Avatar />
          <div className="na-card-stack">
            {m.work && <WorkCard work={m.work} />}
            {m.text && <div className="na-bubble na-bubble-bot">{botText(m.text)}</div>}
            <p className="na-card-stack-hint">Matches are on your board → tap one to focus.</p>
            {m.projects.map((p, cardIndex) => (
              <button
                key={p.id}
                type="button"
                className={`na-project-card-btn${cardIndex === 0 && p.fit ? ' na-card-best' : ''}`}
                onClick={() => onOpenProject(p.id)}
              >
                {cardIndex === 0 && p.fit && <span className="na-best-band">★ Best fit for you</span>}
                <span className="na-card-left">
                  <span className="na-card-name">{p.name}</span>
                  <span className="na-card-sub">
                    {p.builder} · {p.location}
                  </span>
                  {p.fit && <span className={`na-fit na-fit-${p.fitTone ?? 'neutral'} na-card-fit`}>{p.fit}</span>}
                </span>
                <span className="na-card-right">
                  <span className="na-card-price">{p.price}</span>
                  {p.tag && <span className="na-tag">{p.tag}</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (m.quickReplies?.length) {
    return (
      <div className="na-msg">
        <div className="na-msg-row">
          <Avatar />
          <div className="na-msg-stack">
            {m.text && <div className="na-bubble na-bubble-bot">{botText(m.text)}</div>}
            <QuickReplyRow replies={m.quickReplies} />
          </div>
        </div>
      </div>
    );
  }

  const isUser = m.role === 'user';
  if (!isUser && m.work) {
    return (
      <div className="na-msg">
        <div className="na-msg-row">
          <Avatar />
          <div className="na-card-stack">
            <WorkCard work={m.work} />
            {m.text && <div className="na-bubble na-bubble-bot">{botText(m.text)}</div>}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="na-msg">
      <div className={isUser ? 'na-msg-row na-msg-row-user' : 'na-msg-row'}>
        {!isUser && <Avatar />}
        <div className={isUser ? 'na-bubble na-bubble-user' : 'na-bubble na-bubble-bot'}>
          {!isUser && m.text ? (latest ? <RevealText text={m.text} /> : botText(m.text)) : m.text}
        </div>
      </div>
    </div>
  );
}

/** Receipt a closed sheet left behind — tap to summon it again. */
function ReceiptLine({ receipt }: { receipt: NonNullable<ChatMessage['receipt']> }) {
  const { openBoardProject, setBoardView, setBoardSheetOpen } = useAdvisor();
  const reopen = () => {
    if (receipt.kind === 'project' && receipt.projectId) {
      openBoardProject(receipt.projectId);
    } else if (receipt.kind === 'compare') {
      setBoardView('compare');
    } else {
      setBoardView('visit');
    }
    setBoardSheetOpen(true);
  };
  return (
    <button type="button" className="ot-receipt" onClick={reopen}>
      <span className="ot-receipt-tick">✓</span>
      <span>
        <b>{receipt.label}</b>
        {receipt.sub ? ` ${receipt.sub}` : ''}
      </span>
    </button>
  );
}

/** "Checked for this answer" — items come only from the turn payload
 *  (see workNoteFromTurn), so this card can never overstate the work. */
function WorkCard({ work }: { work: NonNullable<ChatMessage['work']> }) {
  return (
    <div className="na-work-card" aria-label="What Naya checked for this answer">
      <span className="na-work-kicker">
        <CheckDot /> Checked
      </span>
      <div className="na-work-items">
        {work.items.map((it) => (
          <span key={it.k} className="na-work-item">
            <b>{it.k}</b>
            {it.v}
          </span>
        ))}
      </div>
    </div>
  );
}

function CheckDot() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function QuickReplyRow({ replies }: { replies: QuickReply[] }) {
  const { setPref, briefPhase } = useAdvisor();
  if (briefPhase !== 'collecting') return null;

  const visible = replies.filter((qr) => !isMultiBriefKey(qr.pref));
  if (visible.length === 0) return null;

  return (
    <div className="na-suggestions na-suggestions-inline">
      {visible.map((qr) => (
        <button
          key={`${qr.pref}-${qr.value}`}
          type="button"
          className="na-sug-chip"
          onClick={() => setPref(qr.pref, qr.value)}
        >
          {qr.value}
        </button>
      ))}
    </div>
  );
}

function Avatar() {
  return <div className="na-avatar">N</div>;
}

function TypingIndicator() {
  return (
    <div className="na-msg">
      <div className="na-msg-row">
        <Avatar />
        <div className="na-typing">
          <span />
          <span />
          <span />
        </div>
        <span className="na-typing-label">checking…</span>
      </div>
    </div>
  );
}

/** Ease a whole-number readout from → to over `ms`, cubic-out. */
function tweenCount(from: number, to: number, ms: number, onUpdate: (v: number) => void) {
  const startT = performance.now();
  function step(now: number) {
    const k = Math.min(1, (now - startT) / ms);
    const e = 1 - Math.pow(1 - k, 3);
    onUpdate(Math.round(from + (to - from) * e));
    if (k < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

/** Render a count, with a trailing "+" when the search cap was hit. */
function fmtCount(count: number, capped: boolean): string {
  return capped ? `${count}+` : String(count);
}

/**
 * Shortlisting strip — the bot's narrowing made visible during a brief→results
 * turn. The strip fires a live preview per cumulative filter (in parallel), so
 * as each filter lights up it shows the REAL match count for the brief up to
 * that point ("4 BHK · 6 → ₹60L · 0 → Devanahalli · 0"). Nothing is fabricated:
 * every number is what the Spine would return for that exact filter set; the
 * header count tracks the latest resolved filter and the real shortlist lands
 * with the results that replace this strip. `start` (the catalog size) is only
 * the opening number before the first filter resolves.
 */
function ThinkingStrip({ prefs, start }: { prefs: Preferences; start: number }) {
  const steps = useMemo(() => {
    const s = briefPreviewSteps(prefs);
    return s.length ? s : [{ label: 'your brief', kind: '', preferences: {} }];
  }, [prefs]);

  // Real per-filter counts, filled in as the parallel previews return. Aligned
  // to `steps`; undefined until that step's preview lands.
  const [counts, setCounts] = useState<Array<{ count: number; capped: boolean } | undefined>>(
    () => steps.map(() => undefined),
  );
  const [resolved, setResolved] = useState(0);
  const [settled, setSettled] = useState(false);
  const [count, setCount] = useState(start > 0 ? start : 0);
  const countRef = useRef(start > 0 ? start : 0);

  // Fire one preview per cumulative filter, concurrently — one round-trip of
  // wall-clock, not N. Latest brief wins; unmount aborts in-flight previews.
  useEffect(() => {
    const ctrl = new AbortController();
    setCounts(steps.map(() => undefined));
    steps.forEach((step, i) => {
      if (!Object.keys(step.preferences).length) return;
      void fetchAdvisorPreview(step.preferences, ctrl.signal).then((r) => {
        if (ctrl.signal.aborted) return;
        setCounts((prev) => {
          const next = prev.slice();
          next[i] = { count: r.count, capped: r.capped };
          return next;
        });
      });
    });
    return () => ctrl.abort();
  }, [steps]);

  // Resolution cadence — filters light up in sequence, independent of when the
  // (async) counts arrive, so the animation never stalls waiting on the network.
  useEffect(() => {
    const n = steps.length;
    const timers: number[] = [];
    let cancelled = false;
    setResolved(0);
    setSettled(false);
    steps.forEach((_, i) => {
      timers.push(window.setTimeout(() => !cancelled && setResolved(i + 1), 260 + i * 700));
    });
    timers.push(window.setTimeout(() => !cancelled && setSettled(true), 260 + n * 700 + 220));
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [steps.length]);

  // Ease the header toward the latest resolved filter's real count — whenever a
  // new filter resolves OR its count arrives from the network.
  useEffect(() => {
    if (resolved === 0) return;
    const latest = counts[resolved - 1];
    if (!latest) return;
    tweenCount(countRef.current, latest.count, 500, (v) => {
      countRef.current = v;
      setCount(v);
    });
  }, [resolved, counts]);

  return (
    <div className="na-msg">
      <div className="na-msg-row">
        <Avatar />
        <div className={`na-think${settled ? ' settled' : ''}`}>
          <div className="na-think-head">
            <span className="na-think-pulse" />
            <span className="na-think-title">
              {settled ? 'Shortlisted ' : 'Shortlisting '}
              <span className="soft">{settled ? '— revealing your matches…' : 'your matches…'}</span>
            </span>
            {(count > 0 || start > 0) && (
              <span className="na-think-count">
                <span className="n">
                  {fmtCount(count, Boolean(resolved > 0 && counts[resolved - 1]?.capped))}
                </span>
                <span className="lab">homes</span>
              </span>
            )}
          </div>
          <div className="na-think-filters">
            {steps.map((f, i) => {
              const shown = settled || i < resolved;
              const cls = shown ? 'done' : i === resolved ? 'on' : '';
              const c = counts[i];
              return (
                <div key={i} className={`na-think-filt${cls ? ' ' + cls : ''}`}>
                  <span className="na-think-dot" />
                  <span className="na-think-flabel">
                    {f.label}
                    {f.kind ? <span className="k"> · {f.kind}</span> : null}
                  </span>
                  {shown && c ? (
                    <span className="na-think-fcount">{fmtCount(c.count, c.capped)}</span>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
